import React, { useState, useEffect } from 'react';
import { PortvilleUsageRecord, UsageRecordFormData } from '../../../../types/portvilleBilling';
import { PortvilleRoomWithResidency } from '../../../../types/portvilleRoom';
import { usePortvilleBilling } from '../../../../hooks/admin/usePortvilleBilling';

interface UsageInputFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  editingRecord?: PortvilleUsageRecord | null;
  selectedYear: number;
  selectedMonth: number;
  activeRooms: PortvilleRoomWithResidency[];
}

const UsageInputForm: React.FC<UsageInputFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingRecord,
  selectedYear,
  selectedMonth,
  activeRooms
}) => {
  const [formData, setFormData] = useState<UsageRecordFormData>({
    roomId: '',
    roomNumber: '',
    year: selectedYear,
    month: selectedMonth,
    electricityIndividualUsage: undefined,
    electricityCommonUsage: undefined,
    waterIndividualUsage: undefined,
    waterCommonUsage: undefined
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedFields, setFocusedFields] = useState<Set<string>>(new Set());

  const { saveUsageRecord } = usePortvilleBilling(selectedYear, selectedMonth);

  // 편집 모드일 때 기존 데이터로 폼 초기화
  useEffect(() => {
    if (editingRecord) {
      setFormData({
        roomId: editingRecord.roomId,
        roomNumber: editingRecord.roomNumber,
        year: editingRecord.period.year,
        month: editingRecord.period.month,
        electricityIndividualUsage: editingRecord.electricityIndividualUsage,
        electricityCommonUsage: editingRecord.electricityCommonUsage || 0, // 공용 사용량은 0으로 기본 설정
        waterIndividualUsage: editingRecord.waterIndividualUsage,
        waterCommonUsage: editingRecord.waterCommonUsage || 0 // 공용 사용량은 0으로 기본 설정
      });
    } else {
      setFormData({
        roomId: '',
        roomNumber: '',
        year: selectedYear,
        month: selectedMonth,
        electricityIndividualUsage: undefined,
        electricityCommonUsage: 0, // 공용 사용량은 0으로 기본 설정
        waterIndividualUsage: undefined,
        waterCommonUsage: 0 // 공용 사용량은 0으로 기본 설정
      });
    }
    setErrors({});
  }, [editingRecord, selectedYear, selectedMonth]);

  const handleRoomChange = (roomId: string) => {
    const selectedRoom = activeRooms.find(room => room.id === roomId);
    setFormData(prev => ({
      ...prev,
      roomId,
      roomNumber: selectedRoom?.roomNumber || '',
      year: selectedYear,
      month: selectedMonth
    }));
    setErrors(prev => ({ ...prev, roomId: '' }));
  };

  const handleInputChange = (field: keyof UsageRecordFormData, value: string | number | undefined) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value
    }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.roomId) {
      newErrors.roomId = '호실/시설물을 선택해주세요.';
    }

    // 전기 사용량 검사 (음수 허용)
    if (formData.electricityIndividualUsage !== undefined && formData.electricityIndividualUsage < 0) {
      newErrors.electricityIndividualUsage = '개별 전기 사용량은 0 이상이어야 합니다.';
    }

    // 수도 사용량 검사 (음수 허용)
    if (formData.waterIndividualUsage !== undefined && formData.waterIndividualUsage < 0) {
      newErrors.waterIndividualUsage = '개별 수도 사용량은 0 이상이어야 합니다.';
    }

    // 최소 하나의 사용량은 입력되어야 함
    const hasAnyUsage = formData.electricityIndividualUsage !== undefined || 
                       formData.waterIndividualUsage !== undefined;

    if (!hasAnyUsage) {
      newErrors.electricityIndividualUsage = '최소 하나의 사용량은 입력해주세요.';
      newErrors.waterIndividualUsage = '최소 하나의 사용량은 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await saveUsageRecord(formData, 'admin'); // TODO: 실제 사용자 ID로 변경
      
      if (success) {
        onSave();
      } else {
        alert('사용량 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('사용량 저장 실패:', error);
      alert('사용량 저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* 모달 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {editingRecord ? '사용량 수정' : '사용량 입력'}
            </h2>
            <p className="text-gray-600 mt-1">
              {selectedYear}년 {selectedMonth}월 사용량을 입력합니다
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 모달 내용 */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* 호실/시설물 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                호실/시설물 선택 *
              </label>
              <select
                value={formData.roomId}
                onChange={(e) => handleRoomChange(e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.roomId ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={!!editingRecord} // 편집 모드에서는 호실 변경 불가
              >
                <option value="">호실/시설물을 선택하세요</option>
                {activeRooms.map(room => {
                  let displayText = '';
                  if (room.roomType === '시설물') {
                    // 시설물인 경우
                    displayText = `${room.roomNumber} - ${room.facilityInfo?.facilityName || '시설물'} (${room.facilityInfo?.facilityType || '기타'})`;
                  } else {
                    // 호실인 경우
                    displayText = `${room.roomNumber} - ${room.currentResident?.name || '입주자 없음'}`;
                  }
                  return (
                    <option key={room.id} value={room.id}>
                      {displayText}
                    </option>
                  );
                })}
              </select>
              {errors.roomId && (
                <p className="mt-1 text-sm text-red-600">{errors.roomId}</p>
              )}
            </div>

            {/* 전기 사용량 */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">전기 사용량 (kWh)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    개별 전기 사용량
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.electricityIndividualUsage !== undefined && formData.electricityIndividualUsage !== null ? formData.electricityIndividualUsage.toString() : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('electricityIndividualUsage', value === '' ? undefined : (parseFloat(value) || 0));
                    }}
                    onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'electricityIndividualUsage']))}
                    onBlur={() => setFocusedFields(prev => {
                      const newSet = new Set(prev);
                      newSet.delete('electricityIndividualUsage');
                      return newSet;
                    })}
                    placeholder={focusedFields.has('electricityIndividualUsage') ? '' : '개별 전기 사용량 (음수 허용)'}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      errors.electricityIndividualUsage ? 'border-red-300' : 'border-gray-300'
                    }`}
                    inputMode="decimal"
                  />
                  {errors.electricityIndividualUsage && (
                    <p className="mt-1 text-sm text-red-600">{errors.electricityIndividualUsage}</p>
                  )}
                </div>
              </div>
            </div>

            {/* 수도 사용량 */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">수도 사용량 (톤)</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    개별 수도 사용량
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.waterIndividualUsage !== undefined && formData.waterIndividualUsage !== null ? formData.waterIndividualUsage.toString() : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('waterIndividualUsage', value === '' ? undefined : (parseFloat(value) || 0));
                    }}
                    onFocus={() => setFocusedFields(prev => new Set([...Array.from(prev), 'waterIndividualUsage']))}
                    onBlur={() => setFocusedFields(prev => {
                      const newSet = new Set(prev);
                      newSet.delete('waterIndividualUsage');
                      return newSet;
                    })}
                    placeholder={focusedFields.has('waterIndividualUsage') ? '' : '개별 수도 사용량 (음수 허용)'}
                    className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                      errors.waterIndividualUsage ? 'border-red-300' : 'border-gray-300'
                    }`}
                    inputMode="decimal"
                  />
                  {errors.waterIndividualUsage && (
                    <p className="mt-1 text-sm text-red-600">{errors.waterIndividualUsage}</p>
                  )}
                </div>
              </div>
            </div>

            {/* 안내 메시지 */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    개별 전기와 수도 사용량 중 최소 하나는 입력해야 합니다. 
                    공용 사용량은 건물 전체 사용량에서 자동으로 계산됩니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 모달 푸터 */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? '저장 중...' : editingRecord ? '수정' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsageInputForm;
